// Password Policy Utility
// Handles OTP generation with complexity requirements
export interface PasswordPolicy {
  minLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumbers: boolean
  requireSpecialChars: boolean
}
// Default password policy
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
}
// Generate a random OTP that meets password policy
export const generateSecureOTP = (
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY,
): string => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const numbers = '0123456789'
  const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?'
  let otp = ''
  let availableChars = ''
  // Ensure at least one character from each required category
  if (policy.requireUppercase) {
    otp += uppercase.charAt(Math.floor(Math.random() * uppercase.length))
    availableChars += uppercase
  }
  if (policy.requireLowercase) {
    otp += lowercase.charAt(Math.floor(Math.random() * lowercase.length))
    availableChars += lowercase
  }
  if (policy.requireNumbers) {
    otp += numbers.charAt(Math.floor(Math.random() * numbers.length))
    availableChars += numbers
  }
  if (policy.requireSpecialChars) {
    otp += specialChars.charAt(Math.floor(Math.random() * specialChars.length))
    availableChars += specialChars
  }
  // Fill remaining length with random characters from available pool
  const remainingLength = policy.minLength - otp.length
  for (let i = 0; i < remainingLength; i++) {
    otp += availableChars.charAt(
      Math.floor(Math.random() * availableChars.length),
    )
  }
  // Shuffle the OTP to randomize character positions
  return otp
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('')
}
// Validate if a password meets the policy
export const validatePassword = (
  password: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []
  if (password.length < policy.minLength) {
    errors.push(`Password must be at least ${policy.minLength} characters`)
  }
  if (policy.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }
  if (policy.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }
  if (policy.requireNumbers && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number')
  }
  if (
    policy.requireSpecialChars &&
    !/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)
  ) {
    errors.push('Password must contain at least one special character')
  }
  return {
    isValid: errors.length === 0,
    errors,
  }
}
// Hash password (simulated - in production use bcrypt)
export const hashPassword = (password: string): string => {
  return `hashed_${password}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
